import { useEffect, useState } from "react";

const API_URL = (import.meta?.env?.VITE_API_URL || "http://localhost:8000").replace(/\/$/, "");

// Route guard basado en /api/me/. Distingue estados EXPLÍCITOS para no caer
// en un loop de login (bug histórico):
//   - "anon"           → /api/me/ 401 (sin sesión): se redirige a SSO login.
//   - "sin-funcionario"→ /api/me/ 200 pero funcionario:null (sesión válida,
//                        pero el usuario no tiene perfil de funcionario):
//                        se muestra "sin acceso". NO se redirige a login —
//                        hacerlo volvía a autenticar y caía acá de nuevo →
//                        loop infinito.
//   - "error"          → 5xx / error de red: pantalla de reintento (tampoco
//                        redirige, para no loopear si el backend falla).
//   - "ok"             → sesión + funcionario: se renderiza la app.
export default function RequireAuth({ children }) {
  const [state, setState] = useState("loading"); // loading | ok | sin-funcionario | anon | error

  useEffect(() => {
    let mounted = true;

    fetch(`${API_URL}/api/me/`, { credentials: "include" })
      .then(async (res) => {
        if (!mounted) return;

        if (res.status === 401) {
          localStorage.removeItem("funcionario");
          setState("anon");
          return;
        }

        if (res.ok) {
          const data = await res.json();
          if (data?.funcionario?.id) {
            localStorage.setItem(
              "funcionario",
              JSON.stringify({
                id: data.funcionario.id,
                nombre: data.funcionario.nombre,
                cargo: data.funcionario.cargo,
                username: data.user.username,
                email: data.user.email,
              })
            );
            setState("ok");
          } else {
            // Autenticado, pero sin funcionario asociado.
            localStorage.removeItem("funcionario");
            setState("sin-funcionario");
          }
          return;
        }

        // Otros códigos (5xx, etc.): NO redirigir a login (evita loops).
        localStorage.removeItem("funcionario");
        setState("error");
      })
      .catch(() => {
        if (!mounted) return;
        localStorage.removeItem("funcionario");
        setState("error");
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (state === "loading") {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
        <span>Cargando…</span>
      </div>
    );
  }

  // Sin sesión: iniciar login SSO (único caso donde tiene sentido redirigir).
  if (state === "anon") {
    window.location.assign(`${API_URL}/sso/login`);
    return null;
  }

  // Sesión válida pero sin perfil de funcionario: estado terminal, sin loop.
  if (state === "sin-funcionario") {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", padding: "1rem" }}>
        <div className="card shadow-sm" style={{ maxWidth: 480, width: "100%" }}>
          <div className="card-body text-center p-4">
            <i className="bi bi-shield-lock" style={{ fontSize: "2.5rem", color: "#6c757d" }} aria-hidden="true"></i>
            <h1 className="h5 mt-3 mb-2">Sin acceso</h1>
            <p className="text-muted mb-4">
              Tu sesión está iniciada, pero tu cuenta no tiene un perfil de
              funcionario asociado, así que no podés acceder al panel. Si creés
              que es un error, contactá al administrador del sistema.
            </p>
            <a className="btn btn-outline-secondary" href={`${API_URL}/sso/logout`}>
              Cerrar sesión
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Error de red / backend: no redirigir, ofrecer reintento.
  if (state === "error") {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", padding: "1rem" }}>
        <div className="card shadow-sm" style={{ maxWidth: 480, width: "100%" }}>
          <div className="card-body text-center p-4">
            <i className="bi bi-exclamation-triangle" style={{ fontSize: "2.5rem", color: "#dc3545" }} aria-hidden="true"></i>
            <h1 className="h5 mt-3 mb-2">No se pudo verificar tu sesión</h1>
            <p className="text-muted mb-4">
              Ocurrió un problema al contactar al servidor. Revisá tu conexión e
              intentá de nuevo.
            </p>
            <button className="btn btn-outline-secondary" onClick={() => window.location.reload()}>
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
